// Data helpers for the Flight Routes map: turn logbook flights plus airport
// coordinates into great-circle route lines and airport dots, with filters.

const toRad = (d) => (d * Math.PI) / 180;
const toDeg = (r) => (r * 180) / Math.PI;

// Points along the great circle from a to b ([lat, lng] pairs). Longitudes are
// unwrapped so a route crossing the antimeridian draws as one continuous line.
export function greatCirclePoints(a, b) {
  const [lat1, lng1] = a.map(toRad);
  const [lat2, lng2] = b.map(toRad);
  const d = 2 * Math.asin(Math.sqrt(
    Math.sin((lat2 - lat1) / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin((lng2 - lng1) / 2) ** 2
  ));
  if (d < 1e-6) return [a, b];

  // About one point per degree of arc, enough for a smooth curve
  const segments = Math.min(64, Math.max(4, Math.ceil(toDeg(d))));
  const points = [];
  for (let i = 0; i <= segments; i++) {
    const f = i / segments;
    const A = Math.sin((1 - f) * d) / Math.sin(d);
    const B = Math.sin(f * d) / Math.sin(d);
    const x = A * Math.cos(lat1) * Math.cos(lng1) + B * Math.cos(lat2) * Math.cos(lng2);
    const y = A * Math.cos(lat1) * Math.sin(lng1) + B * Math.cos(lat2) * Math.sin(lng2);
    const z = A * Math.sin(lat1) + B * Math.sin(lat2);
    points.push([toDeg(Math.atan2(z, Math.sqrt(x * x + y * y))), toDeg(Math.atan2(y, x))]);
  }

  for (let i = 1; i < points.length; i++) {
    let lng = points[i][1];
    const prev = points[i - 1][1];
    while (lng - prev > 180) lng -= 360;
    while (lng - prev < -180) lng += 360;
    points[i][1] = lng;
  }
  return points;
}

const upper = (code) => (code ? String(code).trim().toUpperCase() : '');

export const flightYear = (flight) => String(flight.date || '').slice(0, 4);

// Same type the flight log shows: the aircraft record's type, else the free-text column
export const flightAircraftType = (flight) => flight.aircraft_type_lookup || flight.aircraft_type || '';

// Flown = has logged time; anything else (upcoming, or never logged) counts as scheduled
export const isFlown = (flight) => Number(flight.total_time) > 0 || Boolean(flight.actual_in);

const usable = (flight) => {
  const dep = upper(flight.departure_airport);
  const arr = upper(flight.arrival_airport);
  return dep && arr && dep !== arr && !flight.is_cancelled;
};

// Every airport code that appears on a usable flight
export function airportCodes(flights) {
  const codes = new Set();
  for (const flight of flights) {
    if (!usable(flight)) continue;
    codes.add(upper(flight.departure_airport));
    codes.add(upper(flight.arrival_airport));
  }
  return [...codes];
}

// Values for the filter dropdowns: years newest first, aircraft types A–Z
export function filterOptions(flights) {
  const years = new Set();
  const types = new Set();
  for (const flight of flights) {
    if (!usable(flight)) continue;
    const year = flightYear(flight);
    if (/^\d{4}$/.test(year)) years.add(year);
    const type = flightAircraftType(flight);
    if (type) types.add(type);
  }
  return {
    years: [...years].sort((a, b) => b.localeCompare(a)),
    aircraftTypes: [...types].sort((a, b) => a.localeCompare(b)),
  };
}

// filters: { year: 'all' | 'YYYY', aircraftType: 'all' | type, status: 'all' | 'flown' | 'scheduled' }
// airports: { CODE: { latitude, longitude, name, city } }
export function buildRouteData(flights, airports, filters = {}) {
  const { year = 'all', aircraftType = 'all', status = 'all' } = filters;
  const routeMap = new Map();
  const airportMap = new Map();
  const unplaced = new Set();
  let flightCount = 0;

  for (const flight of flights) {
    if (!usable(flight)) continue;
    if (year !== 'all' && flightYear(flight) !== year) continue;
    if (aircraftType !== 'all' && flightAircraftType(flight) !== aircraftType) continue;
    const flown = isFlown(flight);
    if (status === 'flown' && !flown) continue;
    if (status === 'scheduled' && flown) continue;

    const dep = upper(flight.departure_airport);
    const arr = upper(flight.arrival_airport);
    const depInfo = airports[dep];
    const arrInfo = airports[arr];
    if (!depInfo) unplaced.add(dep);
    if (!arrInfo) unplaced.add(arr);
    if (!depInfo || !arrInfo) continue;

    flightCount += 1;

    // A→B and B→A share one line
    const [a, b] = [dep, arr].sort();
    const key = `${a}|${b}`;
    if (!routeMap.has(key)) {
      routeMap.set(key, { key, a, b, flown: 0, scheduled: 0, totalTime: 0 });
    }
    const route = routeMap.get(key);
    if (flown) route.flown += 1; else route.scheduled += 1;
    route.totalTime += parseFloat(flight.total_time || 0);

    for (const code of [dep, arr]) {
      const info = airports[code];
      if (!airportMap.has(code)) {
        airportMap.set(code, {
          code,
          position: [info.latitude, info.longitude],
          name: info.name || '',
          city: info.city || '',
          visits: 0,
        });
      }
      airportMap.get(code).visits += 1;
    }
  }

  const routes = [...routeMap.values()].map(route => {
    const count = route.flown + route.scheduled;
    const pa = airports[route.a];
    const pb = airports[route.b];
    return {
      ...route,
      count,
      scheduledOnly: route.flown === 0,
      positions: greatCirclePoints([pa.latitude, pa.longitude], [pb.latitude, pb.longitude]),
      // Busier routes draw thicker and on top
      weight: Math.min(1.5 + Math.log2(count), 6),
    };
  }).sort((x, y) => x.count - y.count);

  const airportList = [...airportMap.values()].map(airport => ({
    ...airport,
    radius: Math.min(3 + Math.log2(airport.visits) * 1.2, 11),
  })).sort((x, y) => x.visits - y.visits);

  return { routes, airports: airportList, flightCount, unplaced: [...unplaced].sort() };
}

// [[south, west], [north, east]] covering every route point, or null
export function routeBounds(routes, airports) {
  let south = Infinity, west = Infinity, north = -Infinity, east = -Infinity;
  const take = ([lat, lng]) => {
    south = Math.min(south, lat); north = Math.max(north, lat);
    west = Math.min(west, lng); east = Math.max(east, lng);
  };
  routes.forEach(route => route.positions.forEach(take));
  airports.forEach(airport => take(airport.position));
  return Number.isFinite(south) ? [[south, west], [north, east]] : null;
}
